import browserHistory from 'modules/common/browserHistory';
import { DrawerTypes } from 'modules/IMC/common/context/IMCContext.interface';

const MESSAGE_PARAM = 'message';
const DRAWER_PARAM = 'drawer';

/**
 * Drops the `?message=` param of a Help Agent deep link once it can no longer do useful work.
 *
 * The member's question is their own content: leaving it in the address bar makes the link
 * bookmarkable and shareable exactly as sent, keeps it in browser history and in any screenshot
 * attached to a support ticket, and makes a refresh start a second Decagon conversation with the
 * same prefill.
 *
 * Called only where the message is spent, which is why there are two call sites rather than one.
 * A message that was rejected or paused is spent on arrival, so IMCContextProvider drops the param
 * on mount. An accepted one is not spent until Decagon has it, so HelpAgentWidget drops it at the
 * send instead. Stripping an accepted message on mount would break the logged-out email flow: the
 * provider sits above `<App />`, which renders `null` while it verifies the token, so the
 * provider's mount effect runs a full auth bootstrap before `Layout` calls `lastActiveRoute.set()`,
 * which would then persist an href with the message already gone, leaving nothing to prefill
 * after login. The widget cannot strip early by mistake: it is not mounted at all until the member
 * is authenticated (see HelpAgentWidgetContainer).
 *
 * `drawer` is deliberately kept, so a bookmark or a refresh still reopens the widget, just with an
 * empty composer, and the URL keeps describing what is on screen. Everything else the member
 * arrived with (`utm_*`, the fragment) is preserved.
 *
 * Replaces through the shared `browserHistory` singleton rather than `window.history`, so
 * `useLocation().search` inside the router stays in step with the address bar. The provider that
 * owns the deep link sits above the `<Router>`, so `useHistory()` is not available to it.
 */
export default function clearDeepLinkMessageParam() {
    const { pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);

    if (!params.has(MESSAGE_PARAM)) {
        return;
    }

    // Match case-insensitively: production's server lowercases this deep link to `helpagent`.
    // Guarding on the drawer keeps the helper idempotent and inert on any URL that is not a Help
    // Agent deep link, so an unrelated `?message=` elsewhere in the app is never touched.
    if (params.get(DRAWER_PARAM)?.toLowerCase() !== DrawerTypes.HELP_AGENT.toLowerCase()) {
        return;
    }

    params.delete(MESSAGE_PARAM);

    // `drawer` is guaranteed by the check above and is never deleted, so the remaining query is
    // never empty and needs no empty-search branch. `hash` is passed explicitly: both a path string
    // with no `#` and an object with the key omitted resolve to an empty fragment, so either would
    // drop one the member arrived with.
    browserHistory.replace({ pathname, search: `?${params.toString()}`, hash });
}
